import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import SocialMediaPostFormDialog from "@/components/dialogs/SocialMediaPostFormDialog";
import api, { fileUrl, downloadUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Download, FileText, Hash, Instagram, Linkedin, Facebook, Twitter, Share2 } from "lucide-react";

const PLATFORM_ICONS = { Instagram, LinkedIn: Linkedin, Facebook, "Twitter/X": Twitter, Other: Share2 };
const FILTERS = ["All", "Instagram", "LinkedIn", "Facebook", "Twitter/X", "Other"];

export default function SocialMedia() {
  const [posts, setPosts] = useState([]);
  const [platformFilter, setPlatformFilter] = useState("All");
  const [formOpen, setFormOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);

  const load = () => {
    api.get("/social-media").then((r) => setPosts(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(
    () => (platformFilter === "All" ? posts : posts.filter((p) => p.platform === platformFilter)),
    [posts, platformFilter]
  );

  const deletePost = async (id) => {
    try {
      await api.delete(`/social-media/${id}`);
      toast.success("Post removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openAdd = () => {
    setEditingPost(null);
    setFormOpen(true);
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setFormOpen(true);
  };

  return (
    <DashboardLayout title="Social Media">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <Select value={platformFilter} onValueChange={setPlatformFilter}>
          <SelectTrigger data-testid="social-platform-filter" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            {FILTERS.map((f) => <SelectItem key={f} value={f}>{f === "All" ? "All Platforms" : f}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button data-testid="add-social-post-button" onClick={openAdd} className="bg-[#76C121] hover:bg-[#5C9819] text-white">
          <Plus className="h-4 w-4 mr-1.5" /> Add Post
        </Button>
      </div>

      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="social-media-empty-state">
          <Share2 className="h-6 w-6 text-[#4B5563]/40 mb-2" />
          <p className="text-sm text-[#4B5563]">No posts recorded yet. Click "Add Post" to log the first one.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((p) => (
          <PostCard key={p.id} post={p} onEdit={() => openEdit(p)} onDelete={() => deletePost(p.id)} />
        ))}
      </div>

      <SocialMediaPostFormDialog open={formOpen} onOpenChange={setFormOpen} post={editingPost} onSaved={load} />
    </DashboardLayout>
  );
}

function PostCard({ post, onEdit, onDelete }) {
  const PlatformIcon = PLATFORM_ICONS[post.platform] || Share2;
  return (
    <div data-testid={`social-post-${post.id}`} className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden flex flex-col">
      {post.media.length > 0 && (
        <div className="grid grid-cols-3 gap-0.5 bg-[#F8F9FA]">
          {post.media.slice(0, 3).map((m, i) => (
            <a key={m.file_path} href={fileUrl(m.file_path)} target="_blank" rel="noreferrer" className="relative h-24 block overflow-hidden">
              {m.content_type?.startsWith("image/") ? (
                <img src={fileUrl(m.file_path)} alt={m.original_filename} className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full flex items-center justify-center bg-[#1C1C1E]/5"><FileText className="h-5 w-5 text-[#4B5563]" /></div>
              )}
              {i === 2 && post.media.length > 3 && (
                <div className="absolute inset-0 bg-black/55 flex items-center justify-center text-white text-xs font-medium">+{post.media.length - 3} more</div>
              )}
            </a>
          ))}
        </div>
      )}
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center justify-between mb-2">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-[#00823B] bg-[#F7FEE7] px-2 py-1 rounded">
            <PlatformIcon className="h-3 w-3" /> {post.platform}
          </span>
          <div className="flex items-center gap-1.5">
            <button data-testid={`social-edit-${post.id}`} onClick={onEdit} className="h-7 w-7 flex items-center justify-center rounded-md text-[#4B5563] hover:bg-[#F8F9FA] transition-colors duration-200">
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid={`social-delete-${post.id}`} className="h-7 w-7 flex items-center justify-center rounded-md text-red-600 hover:bg-red-50 transition-colors duration-200">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete "{post.title}"?</AlertDialogTitle>
                  <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        <h3 className="font-heading text-sm font-semibold text-[#1C1C1E]">{post.title}</h3>
        {post.content && <p className="text-sm text-[#4B5563] mt-1.5 line-clamp-3 whitespace-pre-wrap">{post.content}</p>}
        {post.hashtags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {post.hashtags.map((h) => (
              <span key={h} className="inline-flex items-center gap-0.5 text-xs text-[#00823B]"><Hash className="h-3 w-3" />{h}</span>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between mt-auto pt-3 text-xs text-[#4B5563]">
          <span>{post.post_date || "—"} · by {post.created_by}</span>
          {post.media.length > 0 && (
            <a href={downloadUrl(post.media[0].file_path, post.media[0].original_filename)} data-testid={`social-download-${post.id}`} title="Download first file">
              <Download className="h-3.5 w-3.5 text-[#00823B]" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
